import React from "react";
import { ChevronRight, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { patientOf, payerOf } from "@/lib/claimReadiness";
import { provOf, isSigned, isGenerated, sourceLabel, isAgentCoding, isRecoding, CODING_LOCK_MSG } from "@/lib/createClaimsModel";
import { titleCaseNameWithCreds } from "@/lib/patientFormat";
import { codingStep } from "@/lib/noteClaim";
import EncounterExpanded from "./EncounterExpanded";
import { TovCell } from "./TovCell";
import { DosVisit } from "./DosVisit";
import { INK, SUB, FAINT, AMBER, billingDiffers } from "./erShared";

const IdCardIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-none" aria-hidden="true"><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M7 15a2.5 2.5 0 0 1 5 0" /><circle cx="9.5" cy="11" r="1.5" /><path d="M15 11h3M15 14h2" /></svg>;

const fmtDos = (iso) => { const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso); return isNaN(d) ? "—" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" }); };
// Part F — "Waiting …" counts days since the visit was added (created_at), not DOS.
const addedDays = (iso) => { if (!iso) return null; const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000); return d >= 0 ? d : null; };

async function copy(text, what) {
  try { await navigator.clipboard.writeText(text); toast.success(`Copied ${what}`); }
  catch { toast.error("Copy isn't allowed here. Select the text and copy it instead."); }
}

function CopyCell({ text, what, id, children }) {
  return (
    <span className="er-cp min-w-0">
      {children}
      {text ? <button type="button" className="er-cpbtn" aria-label={`Copy ${what}`} title={`Copy ${what}`} onClick={(e) => { e.stopPropagation(); copy(text, what); }} data-testid={`er-copy-${id}`}><Copy size={13} /></button> : null}
    </span>
  );
}

const L1 = "block text-[13px] truncate", L2 = "block text-[12px] truncate";

function PhysCell({ n }) {
  const prov = provOf(n) ? titleCaseNameWithCreds(provOf(n)) : "—";
  const br = n.billing_relationship;
  const diff = billingDiffers(br);
  const text = diff ? `${prov} (billing provider: ${br.billing_name})` : provOf(n) ? prov : "";
  return (
    <CopyCell text={text} what="physician" id={`phys-${n.id}`}>
      <span className={L1} style={{ color: INK }}>{prov}</span>
      {br?.status === "missing" ? <span className={L2} style={{ color: AMBER }} title={br.detail || ""} data-testid={`er-row-billing-missing-${n.id}`}>Billing provider missing</span>
        : diff ? <span className={L2} style={{ color: FAINT }} data-testid={`er-row-billing-${n.id}`}>Billing provider · {br.billing_name}</span> : null}
    </CopyCell>
  );
}

function statusOf(n, fresh) {
  const signed = isSigned(n), wait = addedDays(n.created_at);
  if (isAgentCoding(n)) { const s = codingStep(n); return { dot: FAINT, l1: "Coding", c: SUB, l2: s.step ? `Step ${s.step} of ${s.total} · ${s.label}` : s.label, spin: true }; }
  if (isRecoding(n)) return { dot: FAINT, l1: "Recoding", c: SUB, l2: "Codes update when it finishes", spin: true };
  if (fresh) return { dot: "#1E8E4E", l1: "Ready to review", c: "#1E8E4E", l2: "revQ just finished coding" };
  if (!signed) return { dot: "#FF9500", l1: "Not signed", c: AMBER, l2: n.codes_stale ? "Codes out of date" : wait == null ? "" : wait === 0 ? "Waiting since today" : wait === 1 ? "Waiting 1 day" : `Waiting ${wait} days`, warn: n.codes_stale || (wait != null && wait >= 3) };
  if (n.codes_stale) return { dot: "#FF9500", l1: "Codes out of date", c: AMBER, l2: "" };
  return { dot: "#C7C7CB", l1: "Signed", c: SUB, l2: isGenerated(n) ? "Claim generated" : "" };
}

const visitOf = (n) => n.visit_id || "";
const MONO = "'JetBrains Mono',ui-monospace,monospace";

const needsPayerId = (ins) => !!(ins?.payer_name && (ins.needs_review || ins.payer_id_missing || !ins.payer_id || ins.payer_id === "MISSING"));

export function ERRow({ n, cols, tier, grid, showSource, locMode = "off", shortSite, open: openProp, blurred, onToggle: toggle, onSwitch, tovList, onApplyTov, onSign, onManageIns, expandedProps, fresh }) {
  const signed = isSigned(n);
  const coding = isAgentCoding(n);
  const open = openProp && !coding;
  const onToggle = () => (coding ? toast(CODING_LOCK_MSG, { id: `er-lock-${n.id}` }) : toggle());
  const mrnOk = n.match_status === "matched" || n.match_status === "preselected";
  const st = statusOf(n, fresh);
  const sl = n.service_location || {};
  const pos = [sl.code ? `POS ${sl.code}` : "", sl.name || ""].filter(Boolean).join(" · ");
  const site = shortSite ? shortSite(sl) : (sl.facility_name || "");
  const sourceStr = showSource ? sourceLabel(n) : "";
  const cpts = (n.cpt_codes || []).map((c) => c.code), icds = (n.icd_codes || []).map((c) => c.code);
  const codeText = cpts.length || icds.length ? `CPT ${cpts.join(", ")}; ICD-10 ${icds.join(", ")}` : "";
  const name = titleCaseNameWithCreds(patientOf(n));
  const has = (k) => cols.has(k);
  const mrnText = mrnOk && n.mrn ? n.mrn : "No match · read from document";
  const foldPayer = !has("payer");
  const foldTov = !has("tov");
  const foldLocMulti = locMode === "multi" && !has("loc"); // site rides on the visit-type line (tier 2)
  const payerName = payerOf(n);
  const needsId = needsPayerId(n.insurance);
  const payerBtn = payerName
    ? (needsId ? <button type="button" onClick={() => onManageIns?.(n)} className="text-[12px] font-bold inline-flex items-center gap-1 px-2 rounded-[8px] max-w-full hover:brightness-95" style={{ height: 24, maxWidth: "100%", background: "rgba(255,149,0,.12)", color: "#9A5A00" }} title="Payer has no payer id · fix it" data-testid={`er-needsid-${n.id}`}><IdCardIcon /><span className="truncate">Match payer</span></button> : null)
    : <button type="button" onClick={() => onManageIns?.(n)} className="text-[12px] font-bold inline-flex items-center gap-1 px-2 rounded-[8px] max-w-full hover:brightness-95" style={{ height: 26, maxWidth: "100%", background: "rgba(255,149,0,.12)", color: "#9A5A00" }} title="No payer on this visit · add the insurance" data-testid={`er-addins-${n.id}`}><IdCardIcon /><span className="truncate">Add insurance</span></button>;
  const chev = coding ? <Loader2 size={13} className="animate-spin flex-none" style={{ color: FAINT }} data-testid={`er-row-coding-spin-${n.id}`} />
    : <button type="button" className="er-chev flex-none" aria-label={open ? "Collapse" : "Expand"} aria-expanded={open} onClick={(e) => { e.stopPropagation(); onToggle(); }} data-testid={`er-chev-${n.id}`}><ChevronRight size={14} style={{ color: FAINT, transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }} /></button>;
  const status = (
    <span className="flex items-center gap-2 min-w-0 justify-between">
      <span className="min-w-0">
        <span className="flex items-center gap-1.5"><span className="w-[7px] h-[7px] rounded-full flex-none" style={{ background: st.dot }} /><span className="text-[12.5px] font-semibold whitespace-nowrap" style={{ color: st.c }}>{st.l1}</span></span>
        {st.l2 ? <span className="block text-[11px] mt-0.5 ml-[13px] whitespace-nowrap" style={{ color: st.warn ? AMBER : FAINT }} data-testid={`er-row-status-sub-${n.id}`}>{st.l2}</span> : null}
      </span>
      <span className="flex-none" onClick={(e) => e.stopPropagation()}>
        {coding ? <button disabled className="er-signbtn er-review opacity-50 cursor-not-allowed" title={CODING_LOCK_MSG} data-testid={`er-row-coding-${n.id}`}>Coding…</button>
          : open ? <button onClick={onToggle} className="er-signbtn er-review" data-testid={`er-row-close-${n.id}`}>Close</button>
          : !signed ? <button onClick={() => onSign(n)} className="er-signbtn" data-testid={`er-row-sign-${n.id}`}>Sign</button>
          : <button onClick={onToggle} className="er-signbtn er-review" data-testid={`er-row-review-${n.id}`}>Review</button>}
      </span>
    </span>
  );
  const patient = (
    <span className="flex items-start gap-1.5 min-w-0">
      <span className="pt-[2px]">{chev}</span>
      <span className="min-w-0 flex-1">
        <CopyCell text={patientOf(n) ? name : ""} what="patient name" id={`patient-${n.id}`}>
          <span className="block text-[13px] font-bold truncate" style={{ color: INK }} title={name}>{name}</span>
          {tier === 0 ? (
            <>
              <span className={L2} style={{ color: FAINT }}>{[n.date_of_service ? fmtDos(n.date_of_service) : "Needs DOS", visitOf(n), n.tov].filter(Boolean).join(" · ")}</span>
              <span className={L2} style={{ color: FAINT }}>{[provOf(n) ? titleCaseNameWithCreds(provOf(n)) : "", sourceStr, locMode !== "off" && site ? site : ""].filter(Boolean).join(" · ")}</span>
            </>
          ) : <span className="block text-[11.5px] truncate" style={{ color: FAINT }} title={mrnText}><span style={{ fontFamily: mrnOk && n.mrn ? "monospace" : "inherit" }}>{mrnText}</span></span>}
        </CopyCell>
        {foldPayer && payerName ? <span className="block text-[11.5px] truncate" style={{ color: FAINT }} title={payerName} data-testid={`er-row-foldpayer-${n.id}`}>{payerName}</span> : null}
        {(foldPayer || tier === 0) && payerBtn ? <span className="mt-1 inline-flex max-w-full" onClick={(e) => e.stopPropagation()}>{payerBtn}</span> : null}
      </span>
    </span>
  );
  const dosCell = (
    <div className="min-w-0" key="dos">
      <DosVisit n={n} tid="er-row" noDos={<span className="text-[11px] font-semibold rounded-[5px] px-1.5 py-0.5" style={{ background: "#FBF1E0", color: AMBER }} data-testid={`er-row-nodos-${n.id}`}>Needs DOS</span>} />
      {foldTov ? (
        <div className="er-dosfold flex items-center gap-1 min-w-0 mt-0.5" style={{ fontSize: 12, color: FAINT }} onClick={(e) => e.stopPropagation()} data-testid={`er-row-tovfold-${n.id}`}>
          <TovCell note={n} tovList={tovList} onApply={onApplyTov} />
          {(() => { const tail = (locMode === "multi" && site) ? site : sourceStr; return tail ? <span className="truncate">· {tail}</span> : null; })()}
        </div>
      ) : null}
    </div>
  );
  const cells = tier === 0 ? [patient, status] : [
    patient,
    dosCell,
    has("tov") ? (
      <span key="tov" className="min-w-0">
        <TovCell note={n} tovList={tovList} onApply={onApplyTov} />
        {(() => { const parts = []; if (showSource && !has("source")) parts.push(sourceStr); if (foldLocMulti && site) parts.push(site); return parts.filter(Boolean).length ? <span className={L2} style={{ color: FAINT }} title={parts.filter(Boolean).join(" · ")}>{parts.filter(Boolean).join(" · ")}</span> : null; })()}
      </span>
    ) : null,
    has("source") ? <span key="src" className="text-[12.5px] truncate" style={{ color: SUB }}>{sourceLabel(n)}</span> : null,
    has("loc") ? (
      <CopyCell key="loc" text={sl.facility_name || site || pos} what="location" id={`loc-${n.id}`}>
        <span className={L1} style={{ color: site ? INK : FAINT }} title={sl.facility_name || ""} data-testid={`er-row-location-${n.id}`}>{site || "No facility"}</span>
        {pos ? <span className={L2} style={{ color: FAINT }}>{pos}</span> : null}
      </CopyCell>
    ) : null,
    <PhysCell key="phys" n={n} />,
    has("payer") ? (
      <span key="payer" className="flex flex-col items-start gap-0.5 min-w-0" onClick={(e) => e.stopPropagation()} data-testid={`er-row-payer-${n.id}`}>
        {payerName ? <span className="block w-full truncate text-[12.5px]" style={{ color: SUB }} title={payerName}>{payerName}</span> : null}
        {payerName && !needsId ? (() => { const f = n.insurance_secondary?.payer_name ? `2nd · ${n.insurance_secondary.payer_name}` : [n.insurance?.payer_id, n.insurance?.member_id].filter(Boolean).join(" · "); return f ? <span className="block w-full truncate text-[11px]" style={{ color: FAINT }} title={f} data-testid={`er-row-2nd-${n.id}`}>{f}</span> : null; })() : null}
        {payerBtn}
      </span>
    ) : null,
    has("coding") ? (
      <CopyCell key="codes" text={codeText} what="codes" id={`codes-${n.id}`}>
        {coding ? (
          <span className="block text-[12px]" style={{ color: SUB }} data-testid={`er-row-coding-text-${n.id}`}>revQ is coding…</span>
        ) : (
          <>
            <span className="block" style={{ whiteSpace: "nowrap" }} title={cpts.join(", ")} data-testid={`er-row-cpts-${n.id}`}>
              {cpts.length ? (
                <>
                  {cpts.slice(0, 2).map((c, i) => <span key={c} style={{ fontFamily: MONO, fontSize: "12.5px", color: INK, marginRight: i === 0 && cpts.length > 1 ? 8 : 0 }}>{c}</span>)}
                  {cpts.length > 2 ? <span style={{ fontSize: 12, fontWeight: 600, color: SUB, marginLeft: 8 }}>+{cpts.length - 2}</span> : null}
                </>
              ) : <span style={{ color: FAINT }}>—</span>}
            </span>
            {icds.length ? (
              <span className="block mt-0.5" style={{ whiteSpace: "nowrap" }} title={icds.join(", ")} data-testid={`er-row-icds-${n.id}`}>
                {icds.slice(0, 2).map((c, i) => <span key={c} style={{ fontFamily: MONO, fontSize: 12, color: SUB, marginRight: i === 0 && icds.length > 1 ? 8 : 0 }}>{c}</span>)}
                {icds.length > 2 ? <span style={{ fontSize: 12, fontWeight: 600, color: SUB, marginLeft: 8 }}>+{icds.length - 2}</span> : null}
              </span>
            ) : null}
          </>
        )}
      </CopyCell>
    ) : null,
    status,
  ].filter(Boolean);
  return (
    <div className={`er-item ${open ? "open" : ""} ${fresh ? "rq-fresh" : ""}`} onClick={blurred ? () => onSwitch(n) : undefined} data-fresh={fresh ? "true" : undefined} data-testid={`er-row-${n.id}`}>
      <div className={`er-row grid items-center gap-x-3 px-[11px] py-2.5 ${coding ? "cursor-not-allowed" : "cursor-pointer"}`} style={{ gridTemplateColumns: grid }} onClick={onToggle} aria-disabled={coding || undefined} title={coding ? CODING_LOCK_MSG : undefined} data-locked={coding ? "true" : undefined} data-testid={`er-row-toggle-${n.id}`}>
        {cells.map((c, i) => <React.Fragment key={i}>{c}</React.Fragment>)}
      </div>
      {open ? <EncounterExpanded note={n} tovList={tovList} tier={tier} showLoc={!has("loc")} showCoding={!has("coding")} {...expandedProps} /> : null}
    </div>
  );
}
